import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  propertiesApi, documentsApi, keyDatesApi, expensesApi,
  formatCurrency, formatDate, getDaysUntil, getUrgencyLevel,
  DOC_TYPES, DATE_TYPES, getCategoryLabel, DOC_FOLDERS, mapDocTypeToFolder, getFolderLabel
} from '@/lib/api';
import { 
  Building2, ArrowLeft, Edit, Trash2, Plus, FileText, Calendar,
  Download, Upload, DollarSign, AlertTriangle, BarChart3, Eye, X, FolderOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import PropertyModal from '@/components/PropertyModal';
import DocumentModal from '@/components/DocumentModal';
import KeyDateModal from '@/components/KeyDateModal';
import ExpenseModal from '@/components/ExpenseModal';
import FinancialReportUpload from '@/components/FinancialReportUpload';
import MonthlyManualExpenses from '@/components/MonthlyManualExpenses';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [keyDates, setKeyDates] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modals
  const [editPropertyModal, setEditPropertyModal] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);
  const [keyDateModal, setKeyDateModal] = useState(false);
  const [expenseModal, setExpenseModal] = useState(false);
  const [editingKeyDate, setEditingKeyDate] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Delete dialogs
  const [deleteDocDialog, setDeleteDocDialog] = useState(false);
  const [deleteDateDialog, setDeleteDateDialog] = useState(false);
  const [deleteExpenseDialog, setDeleteExpenseDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Document preview
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Document folder filter
  const [selectedFolder, setSelectedFolder] = useState('all');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [propRes, docsRes, datesRes, expensesRes] = await Promise.all([
        propertiesApi.getOne(id),
        documentsApi.getAll(id),
        keyDatesApi.getAll(id),
        expensesApi.getAll(id),
      ]);
      setProperty(propRes.data);
      setDocuments(docsRes.data);
      setKeyDates(datesRes.data);
      setExpenses(expensesRes.data);
    } catch (error) {
      toast.error('Failed to load property details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const totalExpenses = expenses.reduce((sum, e) => sum + e.monthly_amount, 0);

  const handleDeleteDocument = async () => {
    if (!itemToDelete) return;
    try {
      await documentsApi.delete(itemToDelete.id);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    } finally {
      setDeleteDocDialog(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteKeyDate = async () => {
    if (!itemToDelete) return;
    try {
      await keyDatesApi.delete(itemToDelete.id);
      toast.success('Key date deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete key date');
    } finally {
      setDeleteDateDialog(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteExpense = async () => {
    if (!itemToDelete) return;
    try {
      await expensesApi.delete(itemToDelete.id);
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    } finally {
      setDeleteExpenseDialog(false);
      setItemToDelete(null);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await documentsApi.getOne(doc.id);
      const { file_data, filename, content_type } = response.data;
      
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handlePreviewDocument = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    setPreviewData(null);
    
    try {
      const response = await documentsApi.getOne(doc.id);
      const { file_data, content_type } = response.data;
      
      // Check if this is a previewable type
      const isPdf = content_type === 'application/pdf';
      const isImage = content_type.startsWith('image/');
      
      if (isPdf || isImage) {
        // Create blob URL for preview
        const byteCharacters = atob(file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: content_type });
        const url = window.URL.createObjectURL(blob);
        
        setPreviewData({
          url,
          type: isPdf ? 'pdf' : 'image',
          contentType: content_type
        });
      } else {
        // For other file types, fall back to download
        toast.info('Preview not available for this file type. Downloading instead...');
        handleDownloadDocument(doc);
        setPreviewDoc(null);
      }
    } catch (error) {
      toast.error('Failed to load document preview');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewData?.url) {
      window.URL.revokeObjectURL(previewData.url);
    }
    setPreviewDoc(null);
    setPreviewData(null);
  };

  // Get document's folder (mapped from doc_type or folder field)
  const getDocFolder = (doc) => {
    return doc.folder || mapDocTypeToFolder(doc.doc_type);
  };

  // Filter documents by selected folder
  const filteredDocuments = selectedFolder === 'all' 
    ? documents 
    : documents.filter(doc => getDocFolder(doc) === selectedFolder);

  const getUrgencyBadge = (dateString) => {
    const level = getUrgencyLevel(dateString);
    const days = getDaysUntil(dateString);
    
    const variants = {
      overdue: { className: 'bg-red-100 text-red-700 border-red-200', label: 'Overdue' },
      critical: { className: 'bg-red-100 text-red-700 border-red-200', label: `${days} days` },
      warning: { className: 'bg-amber-100 text-amber-700 border-amber-200', label: `${days} days` },
      safe: { className: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: `${days} days` },
    };
    
    return (
      <Badge variant="outline" className={variants[level].className}>
        {variants[level].label}
      </Badge>
    );
  };

  const getDocTypeBadge = (docType) => {
    const colors = {
      insurance: 'bg-blue-100 text-blue-700',
      mortgage: 'bg-purple-100 text-purple-700',
      inspection_report: 'bg-amber-100 text-amber-700',
      letting_statement: 'bg-emerald-100 text-emerald-700',
      other: 'bg-slate-100 text-slate-700',
    };
    
    const label = DOC_TYPES.find(t => t.value === docType)?.label || docType;
    
    return (
      <Badge variant="outline" className={colors[docType] || colors.other}>
        {label}
      </Badge>
    );
  };

  const getFolderBadge = (doc) => {
    const folder = getDocFolder(doc);
    const colors = {
      landlord_reports: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      tenancy_agreements: 'bg-blue-100 text-blue-700 border-blue-200',
      finance_mortgage: 'bg-purple-100 text-purple-700 border-purple-200',
      other: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    
    return (
      <Badge variant="outline" className={colors[folder] || colors.other}>
        {getFolderLabel(folder)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner w-8 h-8"></div>
        </div>
      </AppLayout>
    );
  }

  if (!property) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-lg font-semibold font-display">Property Not Found</h2>
              <p className="text-slate-500 mt-2">This property may have been deleted.</p>
              <Link to="/properties">
                <Button className="mt-4">Back to Properties</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8" data-testid="property-detail-page">
        {/* Back Link */}
        <Link 
          to="/properties" 
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Properties
        </Link>

        {/* Property Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-900 font-display" data-testid="property-address">
                {property.address}
              </h1>
              <Badge 
                variant="outline"
                className={property.status === 'active' 
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                  : 'bg-slate-100 text-slate-500 border-slate-200'
                }
              >
                {property.status === 'active' ? 'Active' : 'Archived'}
              </Badge>
            </div>
            {property.notes && (
              <p className="mt-2 text-slate-600">{property.notes}</p>
            )}
          </div>
          <Button 
            onClick={() => setEditPropertyModal(true)}
            data-testid="edit-property-btn"
            variant="outline"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Property
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Monthly Rent</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="property-rent">
                {formatCurrency(property.monthly_rent)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Total Monthly Expenses</p>
              <p className="text-2xl font-bold text-red-600 mt-1" data-testid="property-expenses">
                {formatCurrency(totalExpenses)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Net Income</p>
              <p className={`text-2xl font-bold mt-1 ${
                property.monthly_rent - totalExpenses >= 0 
                  ? 'text-emerald-600' 
                  : 'text-red-600'
              }`}>
                {formatCurrency(property.monthly_rent - totalExpenses)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <p className="text-sm text-slate-500">Documents</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="property-doc-count">
                {documents.length} <span className="text-sm font-normal text-slate-500">/ 50</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="financials" data-testid="tab-financials">
              <BarChart3 className="w-4 h-4 mr-1" />
              Financial Data
            </TabsTrigger>
            <TabsTrigger value="expenses" data-testid="tab-expenses">
              Monthly Expenses ({expenses.length})
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger value="dates" data-testid="tab-dates">
              Key Dates ({keyDates.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Financial Data Summary */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <CardTitle className="text-lg font-semibold font-display">Financial Data</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('financials')}>
                    Manage
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Upload monthly landlord reports or add manual expenses to track your cashflow.
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setActiveTab('financials')}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Report
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setActiveTab('financials')}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Expenses
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Expenses Summary */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <CardTitle className="text-lg font-semibold font-display">Fixed Expenses</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('expenses')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {expenses.length > 0 ? (
                    <div className="space-y-3">
                      {expenses.slice(0, 4).map((expense) => (
                        <div key={expense.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3 min-w-0">
                            <DollarSign className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-900">
                              {getCategoryLabel(expense.category)}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-red-600">
                            {formatCurrency(expense.monthly_amount)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between p-3 bg-slate-900 text-white rounded-lg mt-2">
                        <span className="text-sm font-medium">Total</span>
                        <span className="text-sm font-bold">{formatCurrency(totalExpenses)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <DollarSign className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No expenses recorded</p>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-3"
                        onClick={() => {
                          setEditingExpense(null);
                          setExpenseModal(true);
                        }}
                      >
                        Add Expense
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming Key Dates */}
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <CardTitle className="text-lg font-semibold font-display">Upcoming Dates</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('dates')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {keyDates.length > 0 ? (
                    <div className="space-y-3">
                      {keyDates.slice(0, 3).map((date) => (
                        <div key={date.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900">
                              {DATE_TYPES.find(t => t.value === date.date_type)?.label || date.date_type}
                            </p>
                            <p className="text-xs text-slate-500">{formatDate(date.due_date)}</p>
                          </div>
                          {getUrgencyBadge(date.due_date)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No key dates set</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Documents */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader className="flex flex-row items-center justify-between pb-4">
                  <CardTitle className="text-lg font-semibold font-display">Documents</CardTitle>
                  <Button size="sm" variant="ghost" onClick={() => setActiveTab('documents')}>
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  {documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.slice(0, 3).map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{doc.filename}</p>
                              <p className="text-xs text-slate-500">{formatDate(doc.upload_date)}</p>
                            </div>
                          </div>
                          {getFolderBadge(doc)}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">No documents yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Financial Data Tab */}
          <TabsContent value="financials">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* PDF Upload Section */}
              <FinancialReportUpload 
                propertyId={id} 
                onDataChange={fetchData}
              />

              {/* Manual Expenses Section */}
              <MonthlyManualExpenses 
                propertyId={id}
                onDataChange={fetchData}
              />
            </div>
          </TabsContent>

          {/* Monthly Expenses Tab */}
          <TabsContent value="expenses">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold font-display">Monthly Expenses</CardTitle>
                <Button 
                  onClick={() => {
                    setEditingExpense(null);
                    setExpenseModal(true);
                  }}
                  data-testid="add-expense-btn"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
              </CardHeader>
              <CardContent>
                {expenses.length > 0 ? (
                  <>
                    <div className="overflow-x-auto" data-testid="expenses-table">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left text-xs font-medium text-slate-500 pb-3">Category</th>
                            <th className="text-left text-xs font-medium text-slate-500 pb-3">Monthly Cost</th>
                            <th className="text-left text-xs font-medium text-slate-500 pb-3">Notes</th>
                            <th className="text-right text-xs font-medium text-slate-500 pb-3">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.map((expense) => (
                            <tr key={expense.id} className="border-b border-slate-100 last:border-0">
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm font-medium text-slate-900">
                                    {getCategoryLabel(expense.category)}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 text-sm font-semibold text-red-600">
                                {formatCurrency(expense.monthly_amount)}
                              </td>
                              <td className="py-3 text-sm text-slate-500 truncate max-w-[200px]">
                                {expense.notes || '-'}
                              </td>
                              <td className="py-3">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setEditingExpense(expense);
                                      setExpenseModal(true);
                                    }}
                                    data-testid={`edit-expense-${expense.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setItemToDelete(expense);
                                      setDeleteExpenseDialog(true);
                                    }}
                                    data-testid={`delete-expense-${expense.id}`}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-50">
                            <td className="py-3 font-semibold text-slate-900">Total</td>
                            <td className="py-3 font-bold text-red-600">{formatCurrency(totalExpenses)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No expenses recorded yet</p>
                    <p className="text-sm text-slate-400 mt-1">Add your monthly expenses to track costs</p>
                    <Button 
                      onClick={() => {
                        setEditingExpense(null);
                        setExpenseModal(true);
                      }}
                      className="mt-4 bg-slate-900 hover:bg-slate-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Expense
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div className="flex items-center gap-4">
                  <CardTitle className="text-lg font-semibold font-display">Documents</CardTitle>
                  {/* Folder Filter */}
                  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5" data-testid="folder-filter">
                    <button
                      onClick={() => setSelectedFolder('all')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        selectedFolder === 'all'
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid="folder-filter-all"
                    >
                      All
                    </button>
                    {DOC_FOLDERS.map(folder => (
                      <button
                        key={folder.value}
                        onClick={() => setSelectedFolder(folder.value)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          selectedFolder === folder.value
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                        data-testid={`folder-filter-${folder.value}`}
                      >
                        {folder.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button 
                  onClick={() => setDocumentModal(true)}
                  data-testid="upload-document-btn"
                  disabled={documents.length >= 50}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </Button>
              </CardHeader>
              <CardContent>
                {filteredDocuments.length > 0 ? (
                  <div className="overflow-x-auto" data-testid="documents-table">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left text-xs font-medium text-slate-500 pb-3">Name</th>
                          <th className="text-left text-xs font-medium text-slate-500 pb-3">Folder</th>
                          <th className="text-left text-xs font-medium text-slate-500 pb-3">Uploaded</th>
                          <th className="text-left text-xs font-medium text-slate-500 pb-3">Notes</th>
                          <th className="text-right text-xs font-medium text-slate-500 pb-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocuments.map((doc) => (
                          <tr key={doc.id} className="border-b border-slate-100 last:border-0">
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-slate-400" />
                                <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                                  {doc.filename}
                                </span>
                              </div>
                            </td>
                            <td className="py-3">{getFolderBadge(doc)}</td>
                            <td className="py-3 text-sm text-slate-500">{formatDate(doc.upload_date)}</td>
                            <td className="py-3 text-sm text-slate-500 truncate max-w-[150px]">
                              {doc.notes || '-'}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePreviewDocument(doc)}
                                  data-testid={`preview-doc-${doc.id}`}
                                  title="Preview"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadDocument(doc)}
                                  data-testid={`download-doc-${doc.id}`}
                                  title="Download"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setItemToDelete(doc);
                                    setDeleteDocDialog(true);
                                  }}
                                  data-testid={`delete-doc-${doc.id}`}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">
                      {selectedFolder === 'all' 
                        ? 'No documents uploaded yet' 
                        : `No documents in ${getFolderLabel(selectedFolder)}`}
                    </p>
                    <Button 
                      onClick={() => setDocumentModal(true)}
                      className="mt-4 bg-slate-900 hover:bg-slate-800"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Key Dates Tab */}
          <TabsContent value="dates">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg font-semibold font-display">Key Dates</CardTitle>
                <Button 
                  onClick={() => {
                    setEditingKeyDate(null);
                    setKeyDateModal(true);
                  }}
                  data-testid="add-key-date-btn"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Key Date
                </Button>
              </CardHeader>
              <CardContent>
                {keyDates.length > 0 ? (
                  <div className="space-y-3" data-testid="key-dates-list">
                    {keyDates.map((date) => (
                      <div 
                        key={date.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <Calendar className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="font-medium text-slate-900">
                              {DATE_TYPES.find(t => t.value === date.date_type)?.label || date.date_type}
                            </p>
                            <p className="text-sm text-slate-500">{formatDate(date.due_date)}</p>
                            {date.notes && (
                              <p className="text-sm text-slate-400 mt-1">{date.notes}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getUrgencyBadge(date.due_date)}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingKeyDate(date);
                              setKeyDateModal(true);
                            }}
                            data-testid={`edit-date-${date.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setItemToDelete(date);
                              setDeleteDateDialog(true);
                            }}
                            data-testid={`delete-date-${date.id}`}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No key dates set</p>
                    <Button 
                      onClick={() => {
                        setEditingKeyDate(null);
                        setKeyDateModal(true);
                      }}
                      className="mt-4 bg-slate-900 hover:bg-slate-800"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Key Date
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <PropertyModal
        open={editPropertyModal}
        onClose={() => setEditPropertyModal(false)}
        property={property}
        onSuccess={() => {
          setEditPropertyModal(false);
          fetchData();
        }}
      />

      <DocumentModal
        open={documentModal}
        onClose={() => setDocumentModal(false)}
        propertyId={id}
        onSuccess={() => {
          setDocumentModal(false);
          fetchData();
        }}
      />

      <KeyDateModal
        open={keyDateModal}
        onClose={() => setKeyDateModal(false)}
        propertyId={id}
        keyDate={editingKeyDate}
        onSuccess={() => {
          setKeyDateModal(false);
          setEditingKeyDate(null);
          fetchData();
        }}
      />

      <ExpenseModal
        open={expenseModal}
        onClose={() => setExpenseModal(false)}
        propertyId={id}
        expense={editingExpense}
        onSuccess={() => {
          setExpenseModal(false);
          setEditingExpense(null);
          fetchData();
        }}
      />

      {/* Delete Document Dialog */}
      <AlertDialog open={deleteDocDialog} onOpenChange={setDeleteDocDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{itemToDelete?.filename}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteDocument}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Key Date Dialog */}
      <AlertDialog open={deleteDateDialog} onOpenChange={setDeleteDateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Key Date</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this key date? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteKeyDate}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Expense Dialog */}
      <AlertDialog open={deleteExpenseDialog} onOpenChange={setDeleteExpenseDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteExpense}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closePreview}
          data-testid="document-preview-modal"
        >
          <div 
            className="relative bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{previewDoc.filename}</p>
                  <p className="text-xs text-slate-500">{getFolderLabel(getDocFolder(previewDoc))}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownloadDocument(previewDoc)}
                  data-testid="preview-download-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closePreview}
                  data-testid="preview-close-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-auto p-4 bg-slate-100 min-h-[400px]">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="spinner w-8 h-8"></div>
                </div>
              ) : previewData?.type === 'pdf' ? (
                <iframe
                  src={previewData.url}
                  className="w-full h-full min-h-[500px] bg-white rounded-lg"
                  title={previewDoc.filename}
                  data-testid="preview-pdf-iframe"
                />
              ) : previewData?.type === 'image' ? (
                <div className="flex items-center justify-center h-full">
                  <img
                    src={previewData.url}
                    alt={previewDoc.filename}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                    data-testid="preview-image"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-500">
                  Preview not available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
